import React, { useState, useMemo, useEffect } from "react";
import client from "../../../shared/api/client";
import { formatDateTime } from "../../../shared/formattingUtils";
import { offsetDateForBrowser } from "../../../shared/timeZoneLogic";
import { toastError } from "../../../shared/toastHelper";
import StatusBadge from "../../../shared/StatusBadge";
import ActionsMenu from "../../shared/components/ActionsMenu";
import TeamSearchBar from "../../TeamManager/components/TeamSearchBar";
import "../../TeamManager/styles/teamManagerStyles.css";
import XCloseIcon from "../../shared/Icons/XCloseIcon";
import SearchIcon from "../../shared/Icons/SearchIcon";
import PlusIcon from "../../shared/Icons/PlusIcon";
import TrashIcon from "../../shared/Icons/TrashIcon";
import ArrowUpRightIcon from "../../shared/Icons/ArrowUpRightIcon";
import SectionHeader from "./SectionHeader";
import PixelTrackingSection from "./PixelTrackingSection";
import { CopyButton, TextInput } from '@opendate-app/setlist';

const StripeSection = ({
  requiresStripeConnect,
  hasAccount,
  chargesEnabled,
  payoutsEnabled,
  onboardingComplete,
  onboardUrl,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);

  if (!requiresStripeConnect) return null;

  const getStatus = () => {
    if (chargesEnabled && payoutsEnabled) return { status: 'connected', label: 'Connected' };
    if (hasAccount) return { status: 'pending', label: 'Setup Incomplete' };
    return { status: 'disconnected', label: 'Not Connected' };
  };

  const getAction = () => {
    if (chargesEnabled) {
      return (
        <a
          href="https://dashboard.stripe.com"
          target="_blank"
          rel="noopener noreferrer"
          className="team-btn-secondary"
          style={{ fontSize: 13, padding: '4px 12px' }}
        >
          Dashboard <span style={{ marginLeft: 4, display: 'inline-flex', verticalAlign: 'middle' }}><ArrowUpRightIcon fill="currentColor" width="14" height="14" /></span>
        </a>
      );
    }
    return (
      <a
        href={onboardUrl}
        className="team-btn-secondary"
        style={{ fontSize: 13, padding: '4px 12px', ...(isConnecting ? { pointerEvents: 'none', opacity: 0.6 } : {}) }}
        onClick={() => setIsConnecting(true)}
      >
        {isConnecting ? 'Connecting...' : hasAccount ? 'Complete Setup' : 'Connect Stripe'}
      </a>
    );
  };

  const { status, label } = getStatus();

  return (
    <>
      <SectionHeader title="Billing & Payments" />
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{
                borderRadius: 8,
                backgroundColor: '#635bff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: 18,
              }}
            >
              S
            </div>
            <div className="team-member-details">
              <span className="team-member-name">Stripe Connect</span>
              <span className="team-member-email">Payment processing and payouts</span>
            </div>
          </div>
        </td>
        <td><StatusBadge status={status} label={label} /></td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>{getAction()}</td>
      </tr>
    </>
  );
};

// Dedicated donation Stripe Connect account. Unlike the ticket Stripe Connect section,
// this is shown for ALL teams (donations are never commingled with other income).
// `showHeader` renders the "Billing & Payments" header only when the ticket Stripe
// section above did not (i.e. domestic teams), so the header appears exactly once.
const DonationSection = ({
  showHeader,
  hasAccount,
  chargesEnabled,
  payoutsEnabled,
  onboardingComplete,
  onboardUrl,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);

  const getStatus = () => {
    if (chargesEnabled && payoutsEnabled) return { status: 'connected', label: 'Connected' };
    if (hasAccount) return { status: 'pending', label: 'Setup Incomplete' };
    return { status: 'disconnected', label: 'Not Connected' };
  };

  const getAction = () => {
    if (chargesEnabled) {
      return (
        <a
          href="https://dashboard.stripe.com"
          target="_blank"
          rel="noopener noreferrer"
          className="team-btn-secondary"
          style={{ fontSize: 13, padding: '4px 12px' }}
        >
          Dashboard <i className="fas fa-external-link-alt" style={{ marginLeft: 4, fontSize: 10 }} />
        </a>
      );
    }
    return (
      <a
        href={onboardUrl}
        className="team-btn-primary"
        style={{ fontSize: 13, padding: '4px 12px', ...(isConnecting ? { pointerEvents: 'none', opacity: 0.6 } : {}) }}
        onClick={() => setIsConnecting(true)}
      >
        {isConnecting ? 'Connecting...' : hasAccount ? 'Complete Setup' : 'Connect Donation Account'}
      </a>
    );
  };

  const getNote = () =>
    'Donations are collected on a dedicated Stripe account, separate from ticket sales';

  const { status, label } = getStatus();

  return (
    <>
      {showHeader && <SectionHeader title="Billing & Payments" />}
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{
                borderRadius: 8,
                backgroundColor: '#635bff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: 16,
              }}
            >
              <i className="fas fa-heart" />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">Donation Account</span>
              <span className="team-member-email">{getNote()}</span>
            </div>
          </div>
        </td>
        <td><StatusBadge status={status} label={label} /></td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>{getAction()}</td>
      </tr>
    </>
  );
};

const SocialSection = ({
  showSocialManager,
  authorizedPages,
  twitterProviderId,
  facebookProviderId,
  onepostPublicKey,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);

  if (!showSocialManager) return null;

  const getSocialIcon = (type) => {
    switch (type) {
      case 'facebook': return 'fab fa-facebook';
      case 'instagram': return 'fab fa-instagram';
      case 'twitter': return 'fab fa-twitter';
      default: return 'fas fa-globe';
    }
  };

  const getSocialColor = (type) => {
    switch (type) {
      case 'facebook': return '#1877f2';
      case 'instagram': return '#e4405f';
      case 'twitter': return '#1da1f2';
      default: return '#717680';
    }
  };

  const handleRemove = async (authorizedPage) => {
    if (!window.confirm(`Remove ${authorizedPage.name}?`)) return;

    try {
      await client.delete(`/authorized_pages/${authorizedPage.id}`);
      window.location.reload();
    } catch {
      toastError('Failed to remove account. Please try again.');
    }
  };

  return (
    <>
      <SectionHeader title="Social Accounts" />
      {authorizedPages.length === 0 ? (
        <tr>
          <td colSpan={3} style={{ paddingLeft: 24, color: 'var(--sl-text-quaternary, #717680)', fontSize: 13 }}>
            No social accounts connected.
          </td>
        </tr>
      ) : (
        authorizedPages.map((page) => (
          <tr key={page.id}>
            <td style={{ paddingLeft: 24 }}>
              <div className="team-member-info">
                <div
                  className="team-avatar"
                  style={{
                    borderRadius: 8,
                    overflow: 'hidden',
                    backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)',
                  }}
                >
                  {page.profile_image_url ? (
                    <img
                      src={page.profile_image_url}
                      alt={page.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <i className={getSocialIcon(page.demodulized_type)} style={{ fontSize: 20, color: getSocialColor(page.demodulized_type) }} />
                    </div>
                  )}
                </div>
                <div className="team-member-details">
                  <span className="team-member-name">
                    <i className={getSocialIcon(page.demodulized_type)} style={{ marginRight: 6, color: getSocialColor(page.demodulized_type) }} />
                    {page.name}
                  </span>
                  <span className="team-member-email">{page.nickname ? `@${page.nickname}` : ''}</span>
                </div>
              </div>
            </td>
            <td><StatusBadge status="connected" label="Connected" /></td>
            <td>
              <ActionsMenu
                actions={[
                  { label: "Remove", icon: <TrashIcon width="16" height="16" />, onClick: () => handleRemove(page), danger: true },
                ]}
                style={{ display: 'inline-block' }}
              />
            </td>
          </tr>
        ))
      )}
      <tr>
        <td colSpan={3} style={{ paddingLeft: 24, paddingTop: 12, paddingBottom: 12 }}>
          <button
            className="team-btn-secondary"
            style={{ fontSize: 13, padding: '4px 12px', border: '1px solid var(--sl-border-primary, #d5d7da)', borderRadius: 'var(--sl-radius-md, 8px)', backgroundColor: 'var(--sl-bg-primary, white)', cursor: 'pointer' }}
            onClick={() => setShowAddModal(true)}
          >
            <span style={{ marginRight: 4, display: 'inline-flex', verticalAlign: 'middle' }}><PlusIcon width="12" height="12" /></span>
            Add Account
          </button>
        </td>
      </tr>
      {showAddModal && (
        <tr>
          <td colSpan={3} style={{ padding: 0 }}>
            <div className="team-modal-overlay" onClick={() => setShowAddModal(false)}>
              <div
                className="team-modal"
                style={{ maxWidth: 400, width: '90%', padding: 0, textAlign: 'left' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="team-modal-header">
                  <h3 className="team-modal-title">Add an account to Opendate</h3>
                  <button className="team-modal-close" onClick={() => setShowAddModal(false)}>
                    <XCloseIcon width="20" height="20" />
                  </button>
                </div>
                <div className="team-modal-body">
                  <a
                    className="text-decoration-none d-block"
                    href={`https://api.getonepost.com/users/auth/facebook?provider_id=${facebookProviderId}&public_key=${onepostPublicKey}`}
                    style={{ color: 'var(--sl-text-secondary, #414651)' }}
                  >
                    <div className="card mb-0">
                      <div className="card-body text-center py-4 px-0">
                        <img className="img-fluid pb-2" src="/fb-insta@2x.png" style={{ maxWidth: 113 }} />
                        <strong className="d-block">Facebook & Instagram</strong>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const META_MANAGE_URL = "https://meta.opendate.io";

const MetaAdsHeaderCell = ({ subtitle }) => (
  <td style={{ paddingLeft: 24 }}>
    <div className="team-member-info">
      <div
        className="team-avatar"
        style={{
          borderRadius: 8,
          backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <i className="fab fa-facebook" style={{ fontSize: 20, color: '#1877F2' }} />
      </div>
      <div className="team-member-details">
        <span className="team-member-name">Facebook & Instagram Ads</span>
        <span className="team-member-email">{subtitle}</span>
      </div>
    </div>
  </td>
);

export const MetaAdsSection = ({ metaAdsAccess }) => {
  const connected = metaAdsAccess?.status === 'connected';
  const subtitle = connected && metaAdsAccess.lastSyncedAt
    ? `Last synced ${formatDateTime(offsetDateForBrowser(metaAdsAccess.lastSyncedAt))}`
    : 'Ad campaign performance tracking';

  return (
    <>
      <SectionHeader title="Social Ads" />
      <tr>
        <MetaAdsHeaderCell subtitle={subtitle} />
        <td>
          <StatusBadge
            status={connected ? 'connected' : 'disconnected'}
            label={connected ? 'Connected' : 'Not Connected'}
          />
        </td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>
          <a href={META_MANAGE_URL} className="team-btn-secondary" style={{ fontSize: 13, padding: '4px 12px' }}>
            {connected ? 'Configure' : 'Connect Now'}
          </a>
        </td>
      </tr>
    </>
  );
};

const TIXEL_SIGNUP_URL = 'https://tixel.com';

const TixelCopyField = ({ label, description, value }) => (
  <div className="team-form-group">
    <label className="team-form-label">{label}</label>
    <p style={{ fontSize: 12, color: 'var(--sl-text-quaternary, #717680)', marginTop: 0, marginBottom: 8 }}>
      {description}
    </p>
    <TextInput
      aria-label={label}
      value={value || ''}
      readOnly
      style={{ fontFamily: 'monospace' }}
      trailingButton={<CopyButton value={value} disabled={!value} />}
    />
  </div>
);

const TixelRow = ({ tixelId, tixelIntegrationPath, tixelWebhookUrl }) => {
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  const isConnected = Boolean(tixelId);

  useEffect(() => {
    if (isConnected && sessionStorage.getItem('tixel_just_enabled') === '1') {
      sessionStorage.removeItem('tixel_just_enabled');
      setShowManageModal(true);
    }
  }, [isConnected]);

  return (
    <>
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: '#1a1a1a' }}
            >
              <img
                src="/tixel_logo.svg"
                alt="Tixel"
                style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }}
              />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">Tixel</span>
              <span className="team-member-email">Resale marketplace integration</span>
            </div>
          </div>
        </td>
        <td>
          <StatusBadge
            status={isConnected ? 'connected' : 'disconnected'}
            label={isConnected ? 'Connected' : 'Not Connected'}
          />
        </td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>
          {isConnected ? (
            <button
              type="button"
              className="team-btn-secondary"
              style={{ fontSize: 13, padding: '4px 12px' }}
              onClick={() => setShowManageModal(true)}
            >
              Settings
            </button>
          ) : (
            <button
              type="button"
              className="team-btn-secondary"
              style={{ fontSize: 13, padding: '4px 12px' }}
              onClick={() => setShowEnableModal(true)}
            >
              Connect
            </button>
          )}
        </td>
      </tr>
      {showEnableModal && (
        <tr>
          <td colSpan={3} style={{ padding: 0 }}>
            <div className="team-modal-overlay" onClick={() => setShowEnableModal(false)}>
              <div
                className="team-modal"
                style={{ maxWidth: 480, width: '90%', padding: 0, textAlign: 'left' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="team-modal-header">
                  <h3 className="team-modal-title">Enable Tixel</h3>
                  <button className="team-modal-close" onClick={() => setShowEnableModal(false)}>
                    <XCloseIcon width="20" height="20" />
                  </button>
                </div>
                <div className="team-modal-body">
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--sl-text-secondary, #414651)', lineHeight: 1.5 }}>
                    Enabling Tixel will mark your team as resale-eligible and generate a Tixel ID
                    that you'll provide to Tixel so they can list your events on their resale
                    marketplace.
                  </p>
                  <p style={{ marginTop: 12, fontSize: 14, color: 'var(--sl-text-secondary, #414651)', lineHeight: 1.5 }}>
                    If you don't already have a Tixel account,{' '}
                    <a href={TIXEL_SIGNUP_URL} target="_blank" rel="noopener noreferrer">
                      create one here
                    </a>
                    .
                  </p>
                </div>
                <div className="team-modal-footer">
                  <button className="team-btn-secondary" onClick={() => setShowEnableModal(false)}>
                    Cancel
                  </button>
                  <form
                    action={tixelIntegrationPath}
                    method="post"
                    style={{ display: 'inline' }}
                    onSubmit={() => sessionStorage.setItem('tixel_just_enabled', '1')}
                  >
                    <input type="hidden" name="authenticity_token" value={csrfToken} />
                    <button type="submit" className="team-btn-primary">Enable</button>
                  </form>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      {showManageModal && (
        <tr>
          <td colSpan={3} style={{ padding: 0 }}>
            <div className="team-modal-overlay" onClick={() => setShowManageModal(false)}>
              <div
                className="team-modal"
                style={{ maxWidth: 520, width: '90%', padding: 0, textAlign: 'left' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="team-modal-header">
                  <h3 className="team-modal-title">Tixel</h3>
                  <button className="team-modal-close" onClick={() => setShowManageModal(false)}>
                    <XCloseIcon width="20" height="20" />
                  </button>
                </div>
                <div className="team-modal-body">
                  <TixelCopyField
                    label="Tixel ID"
                    description="Use this ID in Tixel to sync your resale-eligible events, and paste it as the fan data webhook secret."
                    value={tixelId}
                  />
                  <TixelCopyField
                    label="Fan data webhook URL"
                    description="Paste this into the fan data webhook URL on your Tixel integrations page to collect waitlist joiners as fans."
                    value={tixelWebhookUrl}
                  />
                  <p style={{ marginTop: 16, fontSize: 14, color: 'var(--sl-text-secondary, #414651)' }}>
                    <a href={TIXEL_SIGNUP_URL} target="_blank" rel="noopener noreferrer">
                      Open Tixel <span style={{ marginLeft: 4, display: 'inline-flex', verticalAlign: 'middle' }}><ArrowUpRightIcon fill="currentColor" width="14" height="14" /></span>
                    </a>
                  </p>
                </div>
                <div className="team-modal-footer">
                  <button className="team-btn-secondary" onClick={() => setShowManageModal(false)}>
                    Close
                  </button>
                  <form
                    action={tixelIntegrationPath}
                    method="post"
                    style={{ display: 'inline' }}
                    onSubmit={(e) => {
                      if (!window.confirm('Disconnect Tixel? Your Tixel ID will be revoked and resale eligibility will be turned off.')) {
                        e.preventDefault();
                      }
                    }}
                  >
                    <input type="hidden" name="_method" value="delete" />
                    <input type="hidden" name="authenticity_token" value={csrfToken} />
                    <button type="submit" className="team-btn-danger">Disconnect</button>
                  </form>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const DoMoreRow = ({ doMoreEnabled, doMoreIntegrationPath }) => {
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

  return (
    <>
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: '#1a1a1a' }}
            >
              <img
                src="/domore_logo.webp"
                alt="DoMore"
                style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }}
              />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">DoMore</span>
              <span className="team-member-email">Complimentary ticket integration</span>
            </div>
          </div>
        </td>
        <td>
          <StatusBadge
            status={doMoreEnabled ? 'connected' : 'disconnected'}
            label={doMoreEnabled ? 'Connected' : 'Not Connected'}
          />
        </td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>
          {doMoreEnabled ? (
            <button
              type="button"
              className="team-btn-secondary"
              style={{ fontSize: 13, padding: '4px 12px' }}
              onClick={() => setShowManageModal(true)}
            >
              Settings
            </button>
          ) : (
            <button
              type="button"
              className="team-btn-secondary"
              style={{ fontSize: 13, padding: '4px 12px' }}
              onClick={() => setShowEnableModal(true)}
            >
              Connect
            </button>
          )}
        </td>
      </tr>
      {showEnableModal && (
        <tr>
          <td colSpan={3} style={{ padding: 0 }}>
            <div className="team-modal-overlay" onClick={() => setShowEnableModal(false)}>
              <div
                className="team-modal"
                style={{ maxWidth: 480, width: '90%', padding: 0, textAlign: 'left' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="team-modal-header">
                  <h3 className="team-modal-title">Enable DoMore</h3>
                  <button className="team-modal-close" onClick={() => setShowEnableModal(false)}>
                    <XCloseIcon width="20" height="20" />
                  </button>
                </div>
                <div className="team-modal-body">
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--sl-text-secondary, #414651)', lineHeight: 1.5 }}>
                    Enabling DoMore lets them read your published, public events and their ticket
                    types, and issue complimentary tickets against them. DoMore can only create
                    comps — it can never sell a ticket or take a payment.
                  </p>
                  <p style={{ marginTop: 12, fontSize: 14, color: 'var(--sl-text-secondary, #414651)', lineHeight: 1.5 }}>
                    Comps count against your manifest unless they are issued against an unmanifested
                    ticket type.
                  </p>
                </div>
                <div className="team-modal-footer">
                  <button className="team-btn-secondary" onClick={() => setShowEnableModal(false)}>
                    Cancel
                  </button>
                  <form action={doMoreIntegrationPath} method="post" style={{ display: 'inline' }}>
                    <input type="hidden" name="authenticity_token" value={csrfToken} />
                    <button type="submit" className="team-btn-primary">Enable</button>
                  </form>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      {showManageModal && (
        <tr>
          <td colSpan={3} style={{ padding: 0 }}>
            <div className="team-modal-overlay" onClick={() => setShowManageModal(false)}>
              <div
                className="team-modal"
                style={{ maxWidth: 520, width: '90%', padding: 0, textAlign: 'left' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="team-modal-header">
                  <h3 className="team-modal-title">DoMore</h3>
                  <button className="team-modal-close" onClick={() => setShowManageModal(false)}>
                    <XCloseIcon width="20" height="20" />
                  </button>
                </div>
                <div className="team-modal-body">
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--sl-text-secondary, #414651)', lineHeight: 1.5 }}>
                    DoMore can read your published, public events and their ticket types, and
                    issue complimentary tickets against them. Comps they issue appear in your
                    orders like any other comp.
                  </p>
                </div>
                <div className="team-modal-footer">
                  <button className="team-btn-secondary" onClick={() => setShowManageModal(false)}>
                    Close
                  </button>
                  <form
                    action={doMoreIntegrationPath}
                    method="post"
                    style={{ display: 'inline' }}
                    onSubmit={(e) => {
                      if (!window.confirm('Disconnect DoMore? They will no longer be able to read your events or issue comps.')) {
                        e.preventDefault();
                      }
                    }}
                  >
                    <input type="hidden" name="_method" value="delete" />
                    <input type="hidden" name="authenticity_token" value={csrfToken} />
                    <button type="submit" className="team-btn-danger">Disconnect</button>
                  </form>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const DiceRow = ({ dice, diceIntegrationPath }) => {
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [apiToken, setApiToken] = useState('');

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  const isConnected = Boolean(dice);
  const hasSyncError = Boolean(dice?.lastSyncError);

  const getStatus = () => {
    if (!isConnected) return { status: 'disconnected', label: 'Not Connected' };
    if (hasSyncError) return { status: 'pending', label: 'Needs Attention' };
    return { status: 'connected', label: 'Connected' };
  };

  const { status, label } = getStatus();

  return (
    <>
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: '#ffffff' }}
            >
              <img
                src="/dice_logo.png"
                alt="DICE"
                style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }}
              />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">DICE</span>
              <span className="team-member-email">Sync events and ticket counts</span>
            </div>
          </div>
        </td>
        <td><StatusBadge status={status} label={label} /></td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>
          <button
            type="button"
            className="team-btn-secondary"
            style={{ fontSize: 13, padding: '4px 12px' }}
            onClick={() => (isConnected ? setShowManageModal(true) : setShowConnectModal(true))}
          >
            {isConnected ? 'Settings' : 'Connect'}
          </button>
        </td>
      </tr>
      {showConnectModal && (
        <tr>
          <td colSpan={3} style={{ padding: 0 }}>
            <div className="team-modal-overlay" onClick={() => setShowConnectModal(false)}>
              <div
                className="team-modal"
                style={{ maxWidth: 480, width: '90%', padding: 0, textAlign: 'left' }}
                onClick={(e) => e.stopPropagation()}
              >
                <form action={diceIntegrationPath} method="post">
                  <input type="hidden" name="authenticity_token" value={csrfToken} />
                  <div className="team-modal-header">
                    <h3 className="team-modal-title">Connect DICE</h3>
                    <button type="button" className="team-modal-close" onClick={() => setShowConnectModal(false)}>
                      <XCloseIcon width="20" height="20" />
                    </button>
                  </div>
                  <div className="team-modal-body">
                    <p style={{ margin: 0, fontSize: 14, color: 'var(--sl-text-secondary, #414651)', lineHeight: 1.5 }}>
                      Opendate will add your DICE events to the calendar and keep their ticket counts
                      up to date every hour. Events are matched to your venues by name.
                    </p>
                    <p style={{ marginTop: 12, fontSize: 14, color: 'var(--sl-text-secondary, #414651)', lineHeight: 1.5 }}>
                      Generate an API token in{' '}
                      <a href="https://mio.dice.fm" target="_blank" rel="noopener noreferrer">DICE MIO</a>
                      {' '}and paste it below.
                    </p>
                    <div className="team-form-group" style={{ marginTop: 16 }}>
                      <label className="team-form-label" htmlFor="dice-api-token">API token</label>
                      <TextInput
                        id="dice-api-token"
                        name="api_token"
                        type="password"
                        autoComplete="off"
                        value={apiToken}
                        onChange={(e) => setApiToken(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="team-modal-footer">
                    <button type="button" className="team-btn-secondary" onClick={() => setShowConnectModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="team-btn-primary" disabled={!apiToken.trim()}>
                      Connect
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </td>
        </tr>
      )}
      {showManageModal && (
        <tr>
          <td colSpan={3} style={{ padding: 0 }}>
            <div className="team-modal-overlay" onClick={() => setShowManageModal(false)}>
              <div
                className="team-modal"
                style={{ maxWidth: 520, width: '90%', padding: 0, textAlign: 'left' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="team-modal-header">
                  <h3 className="team-modal-title">DICE</h3>
                  <button className="team-modal-close" onClick={() => setShowManageModal(false)}>
                    <XCloseIcon width="20" height="20" />
                  </button>
                </div>
                <div className="team-modal-body">
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--sl-text-secondary, #414651)', lineHeight: 1.5 }}>
                    {dice.lastSyncedAt
                      ? `Last synced ${formatDateTime(offsetDateForBrowser(dice.lastSyncedAt))}. ${dice.eventCount} DICE events found.`
                      : 'The first sync is running. Your DICE events will appear on the calendar shortly.'}
                  </p>
                  {hasSyncError && (
                    <p style={{ marginTop: 12, fontSize: 14, color: 'var(--sl-text-error-primary, #d92d20)', lineHeight: 1.5 }}>
                      The last sync had a problem: {dice.lastSyncError}.
                    </p>
                  )}
                  {dice.unmatchedVenueNames.length > 0 && (
                    <p style={{ marginTop: 12, fontSize: 14, color: 'var(--sl-text-secondary, #414651)', lineHeight: 1.5 }}>
                      These DICE venues don't match a venue on your team, so their events aren't synced:{' '}
                      <strong>{dice.unmatchedVenueNames.join(', ')}</strong>.
                    </p>
                  )}
                </div>
                <div className="team-modal-footer">
                  <button className="team-btn-secondary" onClick={() => setShowManageModal(false)}>
                    Close
                  </button>
                  <form
                    action={diceIntegrationPath}
                    method="post"
                    style={{ display: 'inline' }}
                    onSubmit={(e) => {
                      if (!window.confirm('Disconnect DICE? Events already on your calendar and their ticket counts will stay, but will stop updating.')) {
                        e.preventDefault();
                      }
                    }}
                  >
                    <input type="hidden" name="_method" value="delete" />
                    <input type="hidden" name="authenticity_token" value={csrfToken} />
                    <button type="submit" className="team-btn-danger">Disconnect</button>
                  </form>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const TicketingSection = ({ eventbriteAccessId, tixelId, tixelIntegrationPath, tixelWebhookUrl, doMoreEnabled, doMoreIntegrationPath, dice, diceIntegrationPath }) => {
  return (
    <>
      <SectionHeader title="POS (Point of Sale)" />
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)' }}
            >
              <img
                src="/toast-logo.png"
                alt="Toast"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">Toast</span>
              <span className="team-member-email">Point of sale integration</span>
            </div>
          </div>
        </td>
        <td>
          <StatusBadge status="info" label="Contact Us" />
        </td>
        <td></td>
      </tr>
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)' }}
            >
              <img
                src="/square-logo.png"
                alt="Square"
                style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }}
              />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">Square</span>
              <span className="team-member-email">Point of sale integration</span>
            </div>
          </div>
        </td>
        <td></td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>
          <a
            href="https://square.opendate.io"
            className="team-btn-secondary"
            style={{ fontSize: 13, padding: '4px 12px' }}
          >
            Configure
          </a>
        </td>
      </tr>
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)' }}
            >
              <img
                src="/clover-logo.png"
                alt="Clover"
                style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }}
              />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">Clover</span>
              <span className="team-member-email">Point of sale integration</span>
            </div>
          </div>
        </td>
        <td></td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>
          <a
            href="https://clover.opendate.io"
            className="team-btn-secondary"
            style={{ fontSize: 13, padding: '4px 12px' }}
          >
            Configure
          </a>
        </td>
      </tr>
      <SectionHeader title="Labor & Scheduling" />
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)' }}
            >
              <img
                src="/homebase-logo.png"
                alt="Homebase"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">Homebase</span>
              <span className="team-member-email">Labor cost & scheduling integration</span>
            </div>
          </div>
        </td>
        <td></td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>
          <a
            href="https://homebase.opendate.io"
            className="team-btn-secondary"
            style={{ fontSize: 13, padding: '4px 12px' }}
          >
            Configure
          </a>
        </td>
      </tr>
      <SectionHeader title="Ticketing Systems" />
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)' }}
            >
              <img
                src="/logo-white-circle.png"
                alt="Eventbrite"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">Eventbrite</span>
              <span className="team-member-email">Sync calendar and ticket sales</span>
            </div>
          </div>
        </td>
        <td>
          <StatusBadge
            status={eventbriteAccessId ? 'connected' : 'disconnected'}
            label={eventbriteAccessId ? 'Connected' : 'Not Connected'}
          />
        </td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>
          {eventbriteAccessId ? (
            <a
              href={`/eventbrite_accesses/${eventbriteAccessId}/edit`}
              className="team-btn-secondary"
              style={{ fontSize: 13, padding: '4px 12px' }}
            >
              Configure
            </a>
          ) : (
            <a
              data-method="post"
              href="/users/auth/eventbrite"
              className="team-btn-secondary"
              style={{ fontSize: 13, padding: '4px 12px' }}
            >
              Connect
            </a>
          )}
        </td>
      </tr>
      <DiceRow dice={dice} diceIntegrationPath={diceIntegrationPath} />
      <TixelRow
        tixelId={tixelId}
        tixelIntegrationPath={tixelIntegrationPath}
        tixelWebhookUrl={tixelWebhookUrl}
      />
      <DoMoreRow
        doMoreEnabled={doMoreEnabled}
        doMoreIntegrationPath={doMoreIntegrationPath}
      />
      <SectionHeader title="Marketing" />
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)' }}
            >
              <img
                src="/toneden-logo.png"
                alt="ToneDen"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">ToneDen</span>
              <span className="team-member-email">Marketing automation</span>
            </div>
          </div>
        </td>
        <td>
          <StatusBadge status="info" label="Coming Soon" />
        </td>
        <td></td>
      </tr>
    </>
  );
};

export const RostrSection = ({ rostr }) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  const { connection, connectionPath, logoUrl } = rostr;
  const returnTo = window.location.pathname;

  const getStatus = () => {
    if (!connection?.connected) return { status: 'disconnected', label: 'Not Connected' };
    if (!connection.proEligible) return { status: 'pending', label: 'Pro Inactive' };
    return { status: 'connected', label: 'Connected' };
  };

  const { status, label } = getStatus();

  return (
    <>
      <SectionHeader title="Artist Data" />
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)' }}
            >
              <img
                src={logoUrl}
                alt="ROSTR"
                style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }}
              />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">ROSTR</span>
              <span className="team-member-email">
                {connection?.connected && connection.linkedByName
                  ? `Connected by ${connection.linkedByName} for your whole team`
                  : 'Artist agency, management, and label for your whole team'}
              </span>
            </div>
          </div>
        </td>
        <td>
          <StatusBadge status={status} label={label} />
        </td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>
          <form
            action={connectionPath}
            method="post"
            style={{ display: 'inline' }}
            onSubmit={(e) => {
              if (connection?.connected && !window.confirm('Disconnect ROSTR for everyone on your team?')) {
                e.preventDefault();
              }
            }}
          >
            {connection?.connected && <input type="hidden" name="_method" value="delete" />}
            <input type="hidden" name="authenticity_token" value={csrfToken} />
            <input type="hidden" name="return_to" value={returnTo} />
            <button
              type="submit"
              className="team-btn-secondary"
              style={{ fontSize: 13, padding: '4px 12px' }}
            >
              {connection?.connected ? 'Disconnect' : 'Connect'}
            </button>
          </form>
        </td>
      </tr>
    </>
  );
};

const IntegrationCard = ({
  team,
  venueOwnerships,
  selectedVenueOwnershipId,
  requiresStripeConnect,
  hasAccount,
  chargesEnabled,
  payoutsEnabled,
  onboardingComplete,
  onboardUrl,
  requirements,
  donationStripeConnect = {},
  showSocialManager,
  authorizedPages,
  twitterProviderId,
  facebookProviderId,
  onepostPublicKey,
  eventbriteAccessId,
  metaAdsAccess,
  tixelId,
  tixelIntegrationPath,
  tixelWebhookUrl,
  doMoreEnabled,
  doMoreIntegrationPath,
  dice,
  diceIntegrationPath,
  rostr,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [venues, setVenues] = useState(venueOwnerships || []);

  const integrationCount = [
    requiresStripeConnect && (chargesEnabled && payoutsEnabled) ? 1 : 0,
    donationStripeConnect.chargesEnabled && donationStripeConnect.payoutsEnabled ? 1 : 0,
    authorizedPages ? authorizedPages.length : 0,
    eventbriteAccessId ? 1 : 0,
    metaAdsAccess && metaAdsAccess.status === 'connected' ? 1 : 0,
    tixelId ? 1 : 0,
    doMoreEnabled ? 1 : 0,
    dice ? 1 : 0,
    rostr?.connection?.connected ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const matchesSearch = useMemo(() => {
    if (!searchTerm.trim()) return { stripe: true, pixelTracking: true, ticketing: true, social: true, metaAds: true, rostr: true };

    const term = searchTerm.trim().toLowerCase();

    const stripeMatch = ['stripe', 'connect', 'payment', 'payout', 'billing', 'donation', 'donations'].some(
      (keyword) => keyword.includes(term)
    );

    const pixelTrackingKeywords = ['pixel', 'tracking', 'google', 'analytics', 'facebook', 'reddit', 'tiktok', 'spotify', 'conversion', 'remarketing', 'opendate', 'referral', 'referrer'];
    const pixelTrackingMatch = pixelTrackingKeywords.some((keyword) => keyword.includes(term));

    const ticketingKeywords = ['eventbrite', 'dice', 'sync', 'calendar', 'ticket', 'toast', 'point of sale', 'toneden', 'marketing', 'tixel', 'resale', 'marketplace', 'domore', 'comp', 'comps', 'complimentary', 'homebase', 'labor', 'scheduling', 'square', 'clover'];
    const ticketingMatch = ticketingKeywords.some((keyword) => keyword.includes(term));

    const metaAdsKeywords = ['meta', 'facebook', 'instagram', 'ads', 'campaign', 'ad spend', 'social ads'];
    const metaAdsMatch = metaAdsKeywords.some((keyword) => keyword.includes(term));

    const socialKeywords = ['social', 'facebook', 'instagram', 'twitter'];
    const socialPageMatch = (authorizedPages || []).some(
      (page) => (page.name || '').toLowerCase().includes(term) ||
        (page.nickname || '').toLowerCase().includes(term)
    );
    const socialMatch = socialKeywords.some((keyword) => keyword.includes(term)) || socialPageMatch;

    const rostrKeywords = ['rostr', 'artist', 'agency', 'agent', 'management', 'manager', 'label', 'representation'];
    const rostrMatch = rostrKeywords.some((keyword) => keyword.includes(term));

    return { stripe: stripeMatch, pixelTracking: pixelTrackingMatch, ticketing: ticketingMatch, social: socialMatch, metaAds: metaAdsMatch, rostr: rostrMatch };
  }, [searchTerm, authorizedPages]);

  const hasAnyResults = matchesSearch.stripe || matchesSearch.pixelTracking || matchesSearch.metaAds || matchesSearch.ticketing || matchesSearch.social || (rostr && matchesSearch.rostr);

  return (
    <div className="team-member-card">
      <div className="team-member-card-header">
        <div className="team-member-card-header-left">
          <div className="team-member-card-title-row">
            <h2 className="team-member-card-title">Integrations</h2>
            <span className="team-member-count-badge">
              {integrationCount} connected
            </span>
          </div>
          <p className="team-member-card-subtitle">
            Manage integrations with other services you use.
          </p>
        </div>
      </div>

      <div className="team-search-filters-row">
        <div className="team-search-filters-left">
          <TeamSearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search integrations..."
          />
        </div>
      </div>

      {!hasAnyResults ? (
        <div className="team-empty-state">
          <div className="team-empty-state-icon">
            <SearchIcon width="24" height="24" />
          </div>
          <div className="team-empty-state-title">No integrations found</div>
          <div className="team-empty-state-text">
            Try adjusting your search
          </div>
        </div>
      ) : (
      <div className="team-table-wrapper team-table-wrapper--integration">
        <table className="team-table">
          <colgroup>
            <col style={{ width: '50%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ paddingLeft: 24 }}>Integration</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {matchesSearch.stripe && (
              <StripeSection
                requiresStripeConnect={requiresStripeConnect}
                hasAccount={hasAccount}
                chargesEnabled={chargesEnabled}
                payoutsEnabled={payoutsEnabled}
                onboardingComplete={onboardingComplete}
                onboardUrl={onboardUrl}
              />
            )}
            {matchesSearch.stripe && (
              <DonationSection
                showHeader={!requiresStripeConnect}
                {...donationStripeConnect}
              />
            )}
            {rostr && matchesSearch.rostr && (
              <RostrSection rostr={rostr} />
            )}
            {matchesSearch.pixelTracking && (
              <PixelTrackingSection
                venues={venues}
                onVenuesChange={setVenues}
                selectedVenueOwnershipId={selectedVenueOwnershipId}
                teamId={team.id}
              />
            )}
            {matchesSearch.metaAds && (
              <MetaAdsSection metaAdsAccess={metaAdsAccess} />
            )}
            {matchesSearch.ticketing && (
              <TicketingSection
                eventbriteAccessId={eventbriteAccessId}
                tixelId={tixelId}
                tixelIntegrationPath={tixelIntegrationPath}
                tixelWebhookUrl={tixelWebhookUrl}
                doMoreEnabled={doMoreEnabled}
                doMoreIntegrationPath={doMoreIntegrationPath}
                dice={dice}
                diceIntegrationPath={diceIntegrationPath}
              />
            )}
            {matchesSearch.social && (
              <SocialSection
                showSocialManager={showSocialManager}
                authorizedPages={authorizedPages || []}
                twitterProviderId={twitterProviderId}
                facebookProviderId={facebookProviderId}
                onepostPublicKey={onepostPublicKey}
              />
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

export default IntegrationCard;
